import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * HeroCosmos
 * Premium cinematic backdrop for the hero:
 *  - Dense multi-layer star field (CSS radial-gradient + canvas twinkle layer)
 *  - Blue-purple nebula glow
 *  - Mouse parallax (subtle)
 *  - Constellation lines around the right column
 *  - Floating golden spark particles
 */
export default function HeroCosmos({ children }) {
  const ref = useRef(null);
  const canvasRef = useRef(null);

  // Mouse parallax — softly map mouse pos to small offsets
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const layer1X = useTransform(sx, [0, 1], [-12, 12]);
  const layer1Y = useTransform(sy, [0, 1], [-8, 8]);
  const layer2X = useTransform(sx, [0, 1], [-22, 22]);
  const layer2Y = useTransform(sy, [0, 1], [-16, 16]);
  const layer3X = useTransform(sx, [0, 1], [-38, 38]);
  const layer3Y = useTransform(sy, [0, 1], [-26, 26]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      mx.set((e.clientX - rect.left) / rect.width);
      my.set((e.clientY - rect.top) / rect.height);
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  // Twinkle canvas layer
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    let raf;
    const stars = [];
    const N = 60;
    const resize = () => {
      const r = cv.parentElement.getBoundingClientRect();
      cv.width = r.width * window.devicePixelRatio;
      cv.height = r.height * window.devicePixelRatio;
      cv.style.width = r.width + "px";
      cv.style.height = r.height + "px";
      stars.length = 0;
      for (let i = 0; i < N; i++) {
        stars.push({
          x: Math.random() * cv.width,
          y: Math.random() * cv.height,
          r: (Math.random() * 1.4 + 0.3) * window.devicePixelRatio,
          phase: Math.random() * Math.PI * 2,
          speed: Math.random() * 0.6 + 0.3,
          gold: Math.random() < 0.18,
        });
      }
    };
    resize();
    window.addEventListener("resize", resize);

    let t0 = performance.now();
    const tick = (t) => {
      const dt = (t - t0) / 1000;
      t0 = t;
      ctx.clearRect(0, 0, cv.width, cv.height);
      for (const s of stars) {
        s.phase += dt * s.speed;
        const a = 0.35 + Math.abs(Math.sin(s.phase)) * 0.65;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = s.gold
          ? `rgba(212,175,55,${a})`
          : `rgba(255,255,255,${a})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div ref={ref} className="relative overflow-hidden">
      {/* nebula glow */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 30% 25%, rgba(99,102,241,0.30), transparent 70%), radial-gradient(45% 40% at 78% 60%, rgba(168,85,247,0.18), transparent 70%), radial-gradient(35% 30% at 65% 10%, rgba(212,175,55,0.10), transparent 70%)",
          filter: "blur(10px)",
        }}
      />

      {/* dense parallax star layers (CSS) */}
      <motion.div
        aria-hidden="true"
        style={{ x: layer1X, y: layer1Y }}
        className="absolute inset-0 pointer-events-none"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(1px 1px at 8% 12%, rgba(255,255,255,0.85) 50%, transparent), radial-gradient(1px 1px at 22% 30%, rgba(255,255,255,0.75) 50%, transparent), radial-gradient(1px 1px at 38% 8%, rgba(255,255,255,0.7) 50%, transparent), radial-gradient(1.5px 1.5px at 52% 22%, rgba(212,175,55,0.6) 50%, transparent), radial-gradient(1px 1px at 67% 14%, rgba(255,255,255,0.7) 50%, transparent), radial-gradient(1px 1px at 80% 30%, rgba(255,255,255,0.6) 50%, transparent), radial-gradient(1.5px 1.5px at 91% 18%, rgba(255,255,255,0.7) 50%, transparent), radial-gradient(1px 1px at 14% 52%, rgba(255,255,255,0.6) 50%, transparent), radial-gradient(1px 1px at 30% 68%, rgba(255,255,255,0.55) 50%, transparent), radial-gradient(1.5px 1.5px at 46% 78%, rgba(212,175,55,0.55) 50%, transparent), radial-gradient(1px 1px at 60% 60%, rgba(255,255,255,0.65) 50%, transparent), radial-gradient(1px 1px at 73% 84%, rgba(255,255,255,0.5) 50%, transparent), radial-gradient(1.5px 1.5px at 88% 70%, rgba(255,255,255,0.6) 50%, transparent), radial-gradient(1px 1px at 95% 92%, rgba(255,255,255,0.5) 50%, transparent), radial-gradient(1px 1px at 5% 88%, rgba(255,255,255,0.5) 50%, transparent)",
          }}
        />
      </motion.div>

      <motion.div
        aria-hidden="true"
        style={{ x: layer2X, y: layer2Y }}
        className="absolute inset-0 pointer-events-none opacity-90"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(0.6px 0.6px at 11% 26%, rgba(255,255,255,0.55) 50%, transparent), radial-gradient(0.7px 0.7px at 27% 14%, rgba(255,255,255,0.55) 50%, transparent), radial-gradient(0.6px 0.6px at 41% 32%, rgba(255,255,255,0.5) 50%, transparent), radial-gradient(0.7px 0.7px at 55% 8%, rgba(255,255,255,0.5) 50%, transparent), radial-gradient(0.6px 0.6px at 70% 24%, rgba(255,255,255,0.5) 50%, transparent), radial-gradient(0.8px 0.8px at 84% 12%, rgba(212,175,55,0.5) 50%, transparent), radial-gradient(0.6px 0.6px at 18% 64%, rgba(255,255,255,0.45) 50%, transparent), radial-gradient(0.7px 0.7px at 36% 80%, rgba(255,255,255,0.5) 50%, transparent), radial-gradient(0.6px 0.6px at 52% 70%, rgba(255,255,255,0.45) 50%, transparent), radial-gradient(0.7px 0.7px at 66% 88%, rgba(255,255,255,0.5) 50%, transparent), radial-gradient(0.8px 0.8px at 82% 56%, rgba(212,175,55,0.45) 50%, transparent), radial-gradient(0.6px 0.6px at 93% 78%, rgba(255,255,255,0.45) 50%, transparent)",
          }}
        />
      </motion.div>

      <motion.div
        aria-hidden="true"
        style={{ x: layer3X, y: layer3Y }}
        className="absolute inset-0 pointer-events-none opacity-80"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(2px 2px at 24% 18%, rgba(255,255,255,0.8) 40%, transparent), radial-gradient(2px 2px at 58% 38%, rgba(212,175,55,0.7) 40%, transparent), radial-gradient(2px 2px at 78% 64%, rgba(255,255,255,0.7) 40%, transparent), radial-gradient(2.5px 2.5px at 16% 78%, rgba(255,255,255,0.75) 40%, transparent), radial-gradient(2px 2px at 88% 26%, rgba(212,175,55,0.6) 40%, transparent)",
            filter: "drop-shadow(0 0 6px rgba(255,255,255,0.5))",
          }}
        />
      </motion.div>

      {/* canvas twinkle layer */}
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
      />

      {/* gold sparks */}
      {[...Array(5)].map((_, i) => (
        <motion.span
          key={i}
          aria-hidden="true"
          className="absolute rounded-full pointer-events-none"
          style={{
            left: `${14 + i * 18}%`,
            top: `${22 + (i % 3) * 26}%`,
            width: 3,
            height: 3,
            background: "#D4AF37",
            boxShadow: "0 0 12px rgba(212,175,55,0.8)",
          }}
          animate={{
            y: [0, -14, 0],
            opacity: [0.2, 0.9, 0.2],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 5 + (i % 3),
            repeat: Infinity,
            delay: i * 0.6,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Constellation SVG anchored to right side */}
      <svg
        aria-hidden="true"
        viewBox="0 0 600 600"
        preserveAspectRatio="none"
        className="absolute right-0 top-0 h-full w-[55%] pointer-events-none hidden lg:block"
      >
        <defs>
          <linearGradient id="conn" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgba(212,175,55,0.6)" />
            <stop offset="100%" stopColor="rgba(212,175,55,0.05)" />
          </linearGradient>
        </defs>
        {/* nodes — reduced for calmer luxury feel */}
        {[
          [90, 80], [180, 140], [340, 200], [430, 90], [500, 180],
          [380, 320], [240, 360], [510, 320], [300, 460],
        ].map(([x, y], i) => (
          <motion.circle
            key={`n-${i}`}
            cx={x}
            cy={y}
            r="1.6"
            fill="rgba(212,175,55,0.85)"
            initial={{ opacity: 0.3 }}
            animate={{ opacity: [0.3, 0.95, 0.3] }}
            transition={{ duration: 4 + (i % 4), repeat: Infinity, delay: i * 0.2 }}
          />
        ))}
        {/* edges — reduced by 50% */}
        {[
          [90, 80, 180, 140],
          [180, 140, 340, 200],
          [340, 200, 430, 90],
          [430, 90, 500, 180],
          [500, 180, 510, 320],
          [510, 320, 380, 320],
          [380, 320, 240, 360],
          [240, 360, 300, 460],
        ].map(([x1, y1, x2, y2], i) => (
          <motion.line
            key={`e-${i}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="url(#conn)"
            strokeWidth="1"
            initial={{ opacity: 0.15 }}
            animate={{ opacity: [0.15, 0.55, 0.15] }}
            transition={{ duration: 6 + (i % 3), repeat: Infinity, delay: i * 0.3 }}
          />
        ))}
      </svg>

      {/* content */}
      <div className="relative z-10">{children}</div>
    </div>
  );
}
